import self from "../img/self.png"
import solutions from '../img/solutions.png';

export let colors = ["#4ade80", "#ededea"];
export let singlePage = true;

const cognivoxUrl = "https://www.cognivox-ai.com/";

export const info = {
    firstName: "Sai Vineeth",
    lastName: "Arumalla",
    initials: "SV",
    position: "Founder & AI Engineer",
    company: "Cognivox-AI",
    companyUrl: cognivoxUrl,
    location: "Washington, USA",
    selfPortrait: self,
    tagline: ["I", "build", "Agentic", "AI", "Systems."],
    linkedin: "https://linkedin.com/in/a-sai-vineeth",

    // Rendered as the hero's passing test-suite preamble
    bootChecks: [
        { line: "GET /portfolio", status: "200 OK", ms: "38ms" },
        { line: "identity.verify(sai_vineeth)", status: "PASS", ms: "12ms" },
        { line: "systems.online()", status: "TRUE", ms: "4ms" },
    ],

    metrics: [
        { value: 10, suffix: "M+", label: "auth requests / day secured" },
        { value: 5000, suffix: "+", label: "TPS sustained in prod" },
        { value: 85, suffix: "%", label: "code coverage driven" },
        { value: 25, suffix: "%", label: "response time reduction" },
    ],

    socials: [
        { link: "https://github.com/Saivineeth147", label: "GitHub", short: "gh", icon: "github" },
        { link: "https://linkedin.com/in/a-sai-vineeth", label: "LinkedIn", short: "in", icon: "linkedin" },
        { link: cognivoxUrl, label: "Cognivox-AI", short: "cognivox", icon: "globe" },
    ],

    about: {
        lead: "Software engineer with 4+ years of experience building production AI and backend systems — RAG pipelines, LLM evaluation, and secure, high-throughput distributed services — plus a full-stack AI platform, founded and built end-to-end.",
        body: [
            "Today I'm a Software Engineer II at Bank of America, where I design retrieval-augmented generation pipelines over internal document sources — evaluating retrieval, ranking, chunking, embedding, and prompt strategies — fine-tune and benchmark open-source LLMs, and deploy the resulting AI services into Java and Spring Boot systems. I've also led performance engineering — load testing, heap-dump analysis, caching, database replication — cutting response times 25% and sustaining 5,000+ TPS, built Kafka-based event streaming for real-time AI processing and customer consent flows, and designed OAuth 2.0 / OIDC capabilities aligned to RFC specifications supporting 10M+ requests daily.",
            "In parallel, I founded and built Cognivox-AI end-to-end: an AI-powered API testing and monitoring platform that turns natural language into executed API tests — generated requests, visual DAG workflows, embeddings-backed failure memory, drift detection, and scheduled monitoring.",
            "I hold an M.S. in Computer Science and Engineering from SUNY Buffalo.",
        ],
    },

    experience: [
        {
            index: "01",
            company: "Cognivox-AI",
            role: "Founder & AI Engineer",
            period: "mar 2026 — present",
            current: true,
            link: cognivoxUrl,
            summary: "AI-powered API testing & monitoring platform for backend and QA teams. Conversational test generation from natural language and imported OpenAPI/Swagger, Postman, WSDL, HAR, and URL definitions. Visual DAG workflow editor with dependency mapping, conditional branches, retries, and scheduled monitoring. Semantic retrieval and memory built on embeddings, pgvector, and RAG-style context retrieval to analyze historical failures, detect drift patterns, and generate self-healing recommendations. Intelligent fuzzing, schema validation, async PDF reporting, and OAuth-based auth with JWT revocation and encrypted credential storage — shipped to web and to desktop via Electron and Tauri with IPC request proxying for CORS-safe execution.",
            stack: ["React", "TypeScript", "FastAPI", "PostgreSQL", "pgvector", "Redis", "Supabase", "Vercel", "Electron", "Tauri"],
        },
        {
            index: "02",
            company: "Bank of America",
            role: "Software Engineer II · via Innova Solutions & Apex Systems",
            period: "jun 2024 — present",
            current: true,
            summary: "Designed retrieval-augmented generation pipelines pairing vector search with internal document sources, systematically evaluating retrieval, ranking, chunking, embedding, and prompt strategies for accuracy and grounding. Fine-tuned and evaluated open-source LLMs against baseline models, then deployed the resulting AI services into Java and Spring Boot systems. Built Kafka-based event streaming for real-time AI processing and for customer consent and authentication data across distributed systems. Led performance engineering — load testing, heap-dump analysis, server tuning, caching, database replication — cutting response times 25% and sustaining 5000+ TPS. Designed OAuth 2.0 / OIDC capabilities aligned with RFC specifications supporting 10M+ requests daily, with Splunk-backed observability across IVR, OAuth, and customer federation. Raised code coverage to 85%, cut production issues 20%, and reduced QA regression time 50% with SOAP UI/Groovy automation.",
            stack: ["Python", "RAG", "LLM Eval", "Java", "Spring", "Kafka", "OAuth 2.0", "OIDC", "Oracle", "Cassandra", "Splunk"],
        },
        {
            index: "03",
            company: "Accenture",
            role: "Software Engineer",
            period: "jan 2021 — jun 2022",
            summary: "Designed and developed Spring Boot microservices with Spring Cloud service discovery and API gateway routing. Secured APIs and enterprise endpoints with OAuth and JWT, optimized data access with Spring Data JPA, and drove quality with JUnit 5, Mockito, and SonarQube.",
            stack: ["Java", "Spring Boot", "Spring Cloud", "REST"],
        },
        {
            index: "04",
            company: "Centuria",
            role: "Software Engineer",
            period: "jan — dec 2020",
            summary: "Designed AEM templates and components, integrated AEM with React single-page applications through headless CMS delivery, and managed OSGi bundle deployments with Live Copies and rollout configurations — 97% issue resolution rate.",
            stack: ["AEM", "Java", "React", "OSGi"],
        },
    ],

    skills: [
        { group: "applied ai & llm", items: ['RAG', 'Semantic Search', 'Embeddings', 'Vector Search', 'LLM Evaluation', 'Model Fine-Tuning', 'Prompt Engineering', 'Agent Workflows', 'Transformers', 'PyTorch', 'TensorFlow', 'FAISS', 'HuggingFace', 'Sentence-Transformers'] },
        { group: "languages", items: ['Java', 'Python', 'TypeScript', 'JavaScript', 'Go', 'C', 'C++'] },
        { group: "backend & apis", items: ['Spring Boot', 'Spring MVC', 'Spring Data JPA', 'Spring Cloud', 'FastAPI', 'REST', 'GraphQL', 'WebSocket', 'SOAP/WSDL', 'OpenAPI/Swagger'] },
        { group: "data & storage", items: ['Oracle SQL/PL-SQL', 'PostgreSQL', 'pgvector', 'Cassandra', 'MySQL', 'MongoDB', 'Redis', 'Supabase'] },
        { group: "distributed systems", items: ['Apache Kafka', 'Event-driven architecture', 'DAG workflows', 'Retries', 'Service discovery', 'API gateway routing'] },
        { group: "security & auth", items: ['OAuth 2.0', 'OpenID Connect', 'JWT', 'CSRF Protection', 'HttpOnly Cookies', 'AES-256'] },
        { group: "cloud / devops", items: ['AWS', 'Docker', 'Kubernetes', 'Maven', 'Git', 'GitHub Actions', 'Railway', 'Vercel', 'Electron', 'Tauri'] },
        { group: "testing & observability", items: ['JUnit', 'Mockito', 'SOAP UI', 'Groovy', 'Splunk', 'Structured Logging', 'Trace Context Propagation', 'Load Testing', 'Heap-Dump Analysis', 'Schema Validation', 'Fuzz Testing', 'Drift Detection'] },
    ],

    certifications: [
        { label: 'AWS Certified Solutions Architect — Associate', icon: solutions },
    ],

    portfolio: [
        {
            slug: "cognivox-ai",
            title: "Cognivox-AI",
            source: cognivoxUrl,
            live: true,
            featured: true,
            ms: "41ms",
            description: "AI-powered API testing & monitoring platform for backend and QA teams. Conversational test generation from natural language — imported OpenAPI/Swagger, Postman, WSDL, HAR, or live URLs become executed workflows with dependency mapping, drift detection, and scheduled monitoring.",
            tech: ["React", "TypeScript", "FastAPI", "PostgreSQL", "Redis", "Supabase"],
            badge: "founder · live"
        },
        {
            slug: "llm-testlab",
            title: "LLM TestLab",
            source: "https://huggingface.co/spaces/vineeth98/llm-test-playground",
            live: true,
            ms: "63ms",
            description: "Evaluation platform for assessing hallucination, safety, consistency, and code-generation quality in AI-mediated outputs — with modules for factual accuracy, prompt-injection detection, and response consistency analysis, plus a dashboard that turns test outcomes into actionable model insights.",
            tech: ["React", "FastAPI", "Python", "HuggingFace"],
            badge: "live"
        },
        {
            slug: "portfolio-assistant",
            title: "Portfolio Assistant",
            source: "https://huggingface.co/spaces/vineeth98/portfolio-assistant",
            live: true,
            ms: "58ms",
            description: "RAG-powered chatbot that answers questions over uploaded documents using semantic retrieval — a PDF, DOCX, TXT, Markdown, and JSON ingestion pipeline feeding custom sentence-transformers embeddings into a FAISS vector store, with Groq-hosted LLMs answering under session-based isolation.",
            tech: ["React", "FastAPI", "FAISS", "Groq"],
            badge: "live"
        }
    ]
}
